import { CSSProperties, useState, useEffect } from 'react';
import { CurrentBrand } from 'modules/Types';
import Avatar from '@mui/material/Avatar';
import useScreen from 'Hooks/useScreen';
import gravatar from 'gravatar';
import BrandsList from './BrandsList/BrandsList';
import MemberPermissionsSelect from './MemberPermissionsSelect';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import ButtonSymphony from 'elements/ButtonSymphony';
import ModalSymphony from 'elements/ModalSymphony';
import { MEMBER_ROLES_INFO, OrganizationRole, UserPermissionOnBrand } from 'modules/Const';
import { clsx } from 'modules/Utils';
import Colors from 'modules/Colors';
import { toastError } from 'modules/Toast';
import { toast } from 'react-toastify';
import { TOAST_SUCCESS_OPTIONS } from '../Organization_constants';
import useUser from 'Hooks/useUser';
import Banner from 'elements/Banner';
import { OrganizationUserBrandUpdate } from 'api/organizationV2Api';

interface EditMemberModalV2Props {
  className?: string;
  isOpen: boolean;
  onClose: () => void;
  onRemoveMember: () => void;
  selectedMember: any; // The selected team member from organizationUsers
}

const EditMemberModalV2 = ({
  className,
  isOpen,
  onClose,
  onRemoveMember,
  selectedMember,
}: EditMemberModalV2Props) => {
  const { mobileView } = useScreen();
  const { currentUser } = useUser();
  const {
    organization,
    organizationUsers,
    updateOrganizationUser,
    isLoadingOrganizationAction,
    hasOrganizationAdminAccess
  } = useOrganizationV2();

  const organizationBrands = organization?.brands || [];

  const {
    first_name: firstName = '',
    last_name: lastName = '',
    email = '',
    role: currentRole = OrganizationRole.MEMBER,
    user_id: userId,
  } = selectedMember || {};

  const [tempRole, setTempRole] = useState<OrganizationRole>(currentRole);
  const [brandsLocal, setBrandsLocal] = useState<CurrentBrand[]>([]);

  const isCurrentUser = currentUser?.id === userId;
  const fullName = firstName && lastName ? `${firstName} ${lastName}` : email;

  // Initialize brands when modal opens or selectedMember changes
  useEffect(() => {
    if (selectedMember && organizationBrands.length > 0) {
      // Get current user's brand permissions from organization.brands
      const userBrands = organizationBrands.map((orgBrand: CurrentBrand) => {
        // Find if this user has access to this brand
        const userAccess = selectedMember.brands?.find((userBrand: any) => userBrand.id === orgBrand.id);
        
        return {
          ...orgBrand,
          permissionLevel: userAccess?.permissionLevel || 'none'
        };
      });

      setBrandsLocal(userBrands);
      setTempRole(currentRole);
    }
  }, [selectedMember, organizationBrands, currentRole]);

  /**
   * Determines if the current user can edit another member's permissions
   * Based on the same logic from the original EditMemberModal
   */
  const canEditMember = (): boolean => {
    if (!selectedMember || !hasOrganizationAdminAccess()) {
      return false;
    }

    // Find current user in organization users
    const currentUserInOrg = organizationUsers?.find(user => user.user_id === currentUser?.id);
    if (!currentUserInOrg) {
      return false;
    }

    const myPriority = MEMBER_ROLES_INFO[currentUserInOrg.role as OrganizationRole].priority;
    const theirPriority = MEMBER_ROLES_INFO[selectedMember.role as OrganizationRole].priority;

    // Special case: Owners and Admins can edit themselves
    if (isCurrentUser) {
      return selectedMember.role === OrganizationRole.ADMINISTRATOR || selectedMember.role === OrganizationRole.OWNER;
    }

    // For editing other members: can only edit if current member's priority is LOWER (numerically)
    // than the target member's priority (e.g., Owner(0) can edit Admin(1))
    return myPriority < theirPriority;
  };

  const handleSaveChanges = async () => {
    if (!selectedMember || !organization?.id) {
      return;
    }

    try {
      let brandsWithPermissions: OrganizationUserBrandUpdate[] = [];

      if (tempRole === OrganizationRole.ADMINISTRATOR) {
        // For Team Admins, give them admin access to all brands automatically
        brandsWithPermissions = organizationBrands.map(brand => ({
          id: brand.id,
          permissionLevel: UserPermissionOnBrand.ADMINISTRATOR
        }));
      } else {
        // For non-admin roles, use the selected brand permissions (only include brands with actual permissions, not 'none')
        brandsWithPermissions = brandsLocal
          .filter(brand => brand.permissionLevel !== 'none')
          .map(brand => ({
            id: brand.id,
            permissionLevel: brand.permissionLevel as UserPermissionOnBrand
          }));
      }

      await updateOrganizationUser({
        organizationId: organization.id,
        userId: userId,
        role: tempRole,
        brands: brandsWithPermissions,
      });

      toast.success('Member permissions updated successfully!', TOAST_SUCCESS_OPTIONS);
      onClose();
    } catch (error: any) {
      let errorMessage = 'Something went wrong updating the member';
      switch (error?.response?.data?.message) {
        case 'User not found in organization':
          errorMessage = 'This user is no longer part of the organization.';
          break;
        case 'Insufficient permissions':
          errorMessage = 'You do not have permission to edit this member.';
          break;
      }
      toastError(errorMessage);
      console.error(error);
    }
  };

  const renderPermissionLevelSelect = () => {
    if (selectedMember?.role === OrganizationRole.OWNER) {
      return null;
    }

    return (
      <div className="permissionLevelSelect" style={{ marginBottom: 16, width: '100%' }}>
        <div className="permissionLevelSelectTitle" style={{ marginBottom: 8, color: Colors.greyDark }}>
          Permission Level
        </div>
        <MemberPermissionsSelect
          value={tempRole}
          onChange={setTempRole}
          disabled={!canEditMember()}
        />
      </div>
    );
  };

  const renderActionButtons = () => {
    return (
      <div className="buttonActionRow" style={sty.buttonActionRow}>
        {canEditMember() && (
          <ButtonSymphony
            className="buttonRemoveMemberEditMemberModal"
            onClick={onRemoveMember}
            color="red"
            variant="outlined"
          >
            Remove Member
          </ButtonSymphony>
        )}
        {canEditMember() ? (
          <ButtonSymphony
            className="buttonSaveChangesEditMemberModal"
            isLoading={isLoadingOrganizationAction}
            onClick={handleSaveChanges}
            disabled={isLoadingOrganizationAction}
          >
            Save changes
          </ButtonSymphony>
        ) : (
          <ButtonSymphony
            className="buttonCloseEditMemberModal"
            style={{ marginTop: 16 }}
            onClick={onClose}
          >
            Close
          </ButtonSymphony>
        )}
      </div>
    );
  };

  if (!selectedMember) {
    return null;
  }

  return (
    <ModalSymphony
      className={clsx('editMemberModalV2', className)}
      style={{ gap: 16 }}
      isOpen={isOpen}
      onClose={onClose}
      title={fullName}
      subtitle="Edit permissions and access to brands."
      headerIcon={
        <Avatar 
          src={gravatar.url(email, { protocol: 'https', d: 'retro' })} 
          sx={{ width: 32, height: 32 }} 
        />
      }
      fullScreen={mobileView}
    >
      {renderPermissionLevelSelect()}
      
      {tempRole === OrganizationRole.ADMINISTRATOR && (
        <Banner 
          className="editMemberModalBanner" 
          color={Colors.purpleLighter} 
          style={{ marginBottom: 16 }}
        >
          As a Team Admin, {firstName || email} will have full access to all brands automatically.
        </Banner>
      )}
      
      {tempRole !== OrganizationRole.ADMINISTRATOR && (
        <BrandsList
          isCurrentUser={isCurrentUser}
          brands={brandsLocal}
          onUpdateBrands={setBrandsLocal}
          canEdit={canEditMember()}
        />
      )}
      
      {renderActionButtons()}
    </ModalSymphony>
  );
};

const sty: Record<string, CSSProperties> = {
  buttonActionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    marginTop: 16,
  },
};

export default EditMemberModalV2;
